/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { ref, onBeforeUnmount } from "vue";

/**
 * Composable for drag and drop logic
 * Handles dragging blocks from grid or basket and dropping them on the grid
 */
export function useDragDrop(gridSettings, blocks, basketBlocks) {
  const draggedBlock = ref(null);
  const draggedFromBasket = ref(false);
  const dragPreview = ref(null);
  const rafId = ref(null);
  const pendingDragEvent = ref(null);
  const gridMetrics = ref(null);
  const draggedElement = ref(null);

  /**
   * Create a custom drag image for better visual feedback
   */
  const createDragImage = (block) => {
    const div = document.createElement("div");
    div.className = "custom-drag-image";
    div.innerHTML = `
      <div style="
        padding: 0.75rem 1rem;
        background: var(--primary-100);
        border: 2px solid var(--primary-500);
        border-radius: 0.5rem;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        opacity: 0.95;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: var(--primary-700);
        white-space: nowrap;
      ">
        <i class="ri-drag-move-2-line"></i>
        <span>${block.title || "Block"}</span>
      </div>
    `;
    div.style.position = "absolute";
    div.style.top = "-1000px";
    div.style.left = "-1000px";
    document.body.appendChild(div);

    // Remove after drag image is captured
    setTimeout(() => div.remove(), 0);

    return div;
  };

  /**
   * Start drag from grid
   */
  const onDragStart = (event, block) => {
    draggedBlock.value = block;
    draggedFromBasket.value = false;
    draggedElement.value = event.target;
    event.dataTransfer.effectAllowed = "move";

    // Add visual feedback to dragged element
    event.target.classList.add("dragging");

    // Set custom drag image
    const dragImage = createDragImage(block);
    event.dataTransfer.setDragImage(dragImage, 20, 20);

    // Cache grid metrics for performance
    const gridContainer = event.target.closest(".grid-container-editor");
    if (gridContainer) {
      const rect = gridContainer.getBoundingClientRect();
      gridMetrics.value = {
        rect,
        cellWidth: rect.width / gridSettings.value.columns,
        cellHeight: rect.height / gridSettings.value.rows,
        offsetX: rect.left,
        offsetY: rect.top,
        maxCol: gridSettings.value.columns,
        maxRow: gridSettings.value.rows,
      };
    }
  };

  /**
   * Start drag from basket
   */
  const onBasketDragStart = (event, block) => {
    draggedBlock.value = block;
    draggedFromBasket.value = true;
    draggedElement.value = event.target;
    event.dataTransfer.effectAllowed = "move";

    // Add visual feedback to dragged element
    event.target.classList.add("dragging");

    // Set custom drag image
    const dragImage = createDragImage(block);
    event.dataTransfer.setDragImage(dragImage, 20, 20);

    // Cache grid metrics for basket drag
    // We need to find the grid container in the DOM
    const gridContainer = document.querySelector(".grid-container-editor");
    if (gridContainer) {
      const rect = gridContainer.getBoundingClientRect();
      gridMetrics.value = {
        rect,
        cellWidth: rect.width / gridSettings.value.columns,
        cellHeight: rect.height / gridSettings.value.rows,
        offsetX: rect.left,
        offsetY: rect.top,
        maxCol: gridSettings.value.columns,
        maxRow: gridSettings.value.rows,
      };
    }
  };

  /**
   * Update drag preview position based on event data
   * Synchronized with browser refresh rate
   * Uses cached grid metrics for optimal performance
   */
  const updateDragPreview = (eventData) => {
    const { clientX, clientY, currentTarget } = eventData;

    // Use cached metrics instead of querying DOM
    if (!gridMetrics.value) {
      return;
    }

    const metrics = gridMetrics.value;

    // Calculate position relative to grid container using cached values
    const x = clientX - metrics.offsetX;
    const y = clientY - metrics.offsetY;

    // Calculate column and row using cached cell dimensions
    let col = Math.floor(x / metrics.cellWidth) + 1;
    let row = Math.floor(y / metrics.cellHeight) + 1;

    // Calculate block size
    let width, height;

    if (draggedFromBasket.value) {
      // Default size for blocks from basket
      width = 1;
      height = 1;
    } else {
      // Use existing size for blocks from grid
      width =
        draggedBlock.value.gridPosition.colEnd -
        draggedBlock.value.gridPosition.colStart;
      height =
        draggedBlock.value.gridPosition.rowEnd -
        draggedBlock.value.gridPosition.rowStart;
    }

    // Ensure the block fits within grid boundaries using cached max values
    const maxCol = metrics.maxCol - width + 1;
    const maxRow = metrics.maxRow - height + 1;
    col = Math.max(1, Math.min(col, maxCol));
    row = Math.max(1, Math.min(row, maxRow));

    // Handle auto-scroll for better UX near edges
    const scrollZone = 50;
    const scrollSpeed = 10;
    const relativeY = clientY - metrics.rect.top;
    const relativeX = clientX - metrics.rect.left;

    if (relativeY < scrollZone && currentTarget.scrollTop > 0) {
      currentTarget.scrollTop -= scrollSpeed;
    } else if (
      relativeY > metrics.rect.height - scrollZone &&
      currentTarget.scrollTop <
        currentTarget.scrollHeight - currentTarget.clientHeight
    ) {
      currentTarget.scrollTop += scrollSpeed;
    }

    if (relativeX < scrollZone && currentTarget.scrollLeft > 0) {
      currentTarget.scrollLeft -= scrollSpeed;
    } else if (
      relativeX > metrics.rect.width - scrollZone &&
      currentTarget.scrollLeft <
        currentTarget.scrollWidth - currentTarget.clientWidth
    ) {
      currentTarget.scrollLeft += scrollSpeed;
    }

    const newPreview = {
      colStart: col,
      colEnd: col + width,
      rowStart: row,
      rowEnd: row + height,
    };

    // Only update if different from current preview
    if (
      !dragPreview.value ||
      dragPreview.value.colStart !== newPreview.colStart ||
      dragPreview.value.colEnd !== newPreview.colEnd ||
      dragPreview.value.rowStart !== newPreview.rowStart ||
      dragPreview.value.rowEnd !== newPreview.rowEnd
    ) {
      dragPreview.value = newPreview;
    }
  };

  /**
   * Handle drag over grid - update preview position
   * Uses requestAnimationFrame for optimal performance
   */
  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    if (!draggedBlock.value) {
      return;
    }

    // Store the event data
    pendingDragEvent.value = {
      clientX: event.clientX,
      clientY: event.clientY,
      currentTarget: event.currentTarget,
    };

    // If already scheduled, don't schedule again
    if (rafId.value) {
      return;
    }

    // Schedule update on next animation frame
    rafId.value = requestAnimationFrame(() => {
      if (pendingDragEvent.value) {
        updateDragPreview(pendingDragEvent.value);
      }
      rafId.value = null;
    });
  };

  /**
   * Handle drag leave - clear preview and cancel pending updates
   * Fixed to properly detect when truly leaving the container
   */
  const onDragLeave = (event) => {
    const container = event.currentTarget;
    const related = event.relatedTarget;

    // Check if we're truly leaving the container (not just entering a child)
    if (!related || !container.contains(related)) {
      dragPreview.value = null;
      pendingDragEvent.value = null;

      // Cancel pending animation frame
      if (rafId.value) {
        cancelAnimationFrame(rafId.value);
        rafId.value = null;
      }
    }
  };

  /**
   * Clean up drag state
   */
  const cleanupDragState = () => {
    // Remove visual feedback from dragged element
    if (draggedElement.value) {
      draggedElement.value.classList.remove("dragging");
      draggedElement.value = null;
    }

    // Reset all drag-related state
    draggedBlock.value = null;
    dragPreview.value = null;
    draggedFromBasket.value = false;
    pendingDragEvent.value = null;
    gridMetrics.value = null;

    // Cancel pending animation frame
    if (rafId.value) {
      cancelAnimationFrame(rafId.value);
      rafId.value = null;
    }
  };

  /**
   * Handle drop - move block to new position
   */
  const onDrop = (event, emitUpdate) => {
    event.preventDefault();

    if (!draggedBlock.value || !dragPreview.value) {
      cleanupDragState();
      return;
    }

    if (draggedFromBasket.value) {
      // Moving from basket to grid
      const basketIndex = basketBlocks.value.findIndex(
        (b) => b.id === draggedBlock.value.id,
      );
      if (basketIndex !== -1) {
        // Remove from basket
        const block = basketBlocks.value.splice(basketIndex, 1)[0];
        // Add position
        block.gridPosition = {
          colStart: dragPreview.value.colStart,
          colEnd: dragPreview.value.colEnd,
          rowStart: dragPreview.value.rowStart,
          rowEnd: dragPreview.value.rowEnd,
        };
        // Add to grid
        blocks.value.push(block);
        if (emitUpdate) {
          emitUpdate();
        }
      }
    } else {
      // Moving within grid
      const block = blocks.value.find((b) => b.id === draggedBlock.value.id);
      if (block) {
        block.gridPosition.colStart = dragPreview.value.colStart;
        block.gridPosition.colEnd = dragPreview.value.colEnd;
        block.gridPosition.rowStart = dragPreview.value.rowStart;
        block.gridPosition.rowEnd = dragPreview.value.rowEnd;

        if (emitUpdate) {
          emitUpdate();
        }
      }
    }

    cleanupDragState();
  };

  /**
   * Handle drag end - cleanup state regardless of success or cancel
   */
  const onDragEnd = () => {
    cleanupDragState();
  };

  // Handle page visibility change during drag
  const handleVisibilityChange = () => {
    if (document.hidden && draggedBlock.value) {
      // User switched tabs or minimized window during drag
      // Clean up to prevent stale state
      cleanupDragState();
    }
  };

  // Add visibility change listener
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Cleanup on unmount
  onBeforeUnmount(() => {
    cleanupDragState();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  });

  return {
    draggedBlock,
    draggedFromBasket,
    dragPreview,
    onDragStart,
    onBasketDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
  };
}
